'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface MarineProtectedArea {
  name: string;
  icon: string;
}

const MarineConservationInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const marineProtectedAreas: MarineProtectedArea[] = [
    { name: 'Paralimni', icon: 'MapPinIcon' },
    { name: 'Ayia Napa', icon: 'MapPinIcon' },
    { name: 'Larnaca', icon: 'MapPinIcon' },
    { name: 'Amathounta', icon: 'MapPinIcon' },
    { name: 'Dasoudi (Limassol)', icon: 'MapPinIcon' },
    { name: 'Geroskipou', icon: 'MapPinIcon' },
  ];

  const responsiblePractices = [
    { text: 'Maintain neutral buoyancy', icon: 'CheckCircleIcon' },
    { text: 'Do not touch or disturb marine life', icon: 'CheckCircleIcon' },
    { text: 'Do not collect objects', icon: 'CheckCircleIcon' },
    { text: 'Use reef-safe sunscreen', icon: 'CheckCircleIcon' },
    { text: 'Remove trash encountered when safe to do so', icon: 'CheckCircleIcon' },
  ];

  const conservationInitiatives = [
    { title: 'Artificial reef habitat projects', icon: 'CubeIcon' },
    { title: 'Sea turtle nesting site protection', icon: 'ShieldCheckIcon' },
    { title: 'Underwater clean-up events', icon: 'HandRaisedIcon' },
    { title: 'Marine research and monitoring', icon: 'BeakerIcon' },
  ];

  if (!isHydrated) {
    return (
      <div className="min-h-screen" style={{ background: '#0A0E14' }}>
        <div className="animate-pulse space-y-8 py-12">
          <div className="container mx-auto px-4">
            <div className="h-12 rounded w-3/4 mx-auto mb-4" style={{ background: '#0F2234' }} />
            <div className="h-64 rounded" style={{ background: '#0F2234' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0A0E14' }}>
      {/* Hero Section */}
      <section
        className="py-16 md:py-20"
        style={{
          background: 'linear-gradient(180deg, #0B1622 0%, #0A0E14 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold mb-6 leading-tight"
              style={{ color: '#EAF4F8' }}
            >
              Marine Conservation &amp; Protected Areas in Cyprus
            </h1>
            <p className="text-lg md:text-xl leading-relaxed" style={{ color: '#C8DDE6' }}>
              Cyprus has established a growing network of Marine Protected Areas and artificial reefs to support marine biodiversity, sustainable fishing, environmental education and responsible diving tourism.
            </p>
          </div>
        </div>
      </section>

      {/* Marine Protected Areas */}
      <section className="py-12" style={{ background: '#0A0E14' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Icon name="GlobeAltIcon" size={32} style={{ color: '#6FD3E0' }} />
                <h2
                  className="text-3xl lg:text-4xl font-headline font-bold"
                  style={{ color: '#EAF4F8' }}
                >
                  Marine Protected Areas with Artificial Reefs
                </h2>
              </div>
              <p className="text-lg max-w-3xl mx-auto" style={{ color: '#C8DDE6' }}>
                Cyprus has designated six Marine Protected Areas with artificial reefs under the national strategy for marine protection. Fishing with any gear is prohibited in these zones.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {marineProtectedAreas.map((area, index) => (
                <div
                  key={index}
                  className="rounded-xl p-6 text-center transition-all duration-200"
                  style={{
                    background: '#0F2234',
                    border: '1px solid rgba(111,211,224,0.15)',
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.border = '1px solid rgba(111,211,224,0.35)';
                    el.style.transform = 'translateY(-3px)';
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.border = '1px solid rgba(111,211,224,0.15)';
                    el.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="flex items-center justify-center mb-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(111,211,224,0.10)' }}
                    >
                      <Icon name={area.icon as any} size={20} style={{ color: '#6FD3E0' }} />
                    </div>
                  </div>
                  <h3
                    className="text-lg font-headline font-semibold"
                    style={{ color: '#EAF4F8' }}
                  >
                    {area.name}
                  </h3>
                </div>
              ))}
            </div>

            {/* MUSAN Highlight */}
            <div
              className="rounded-xl overflow-hidden"
              style={{
                background: '#0F2234',
                border: '1px solid rgba(255,138,61,0.25)',
              }}
            >
              <div className="relative h-64 md:h-80 w-full">
                <AppImage
                  src="/assets/images/shutterstock_2170913323-1770410230316.jpg"
                  alt="Underwater museum MUSAN showing sculptural figures with diver exploring the underwater art installation in Ayia Napa Marine Protected Area"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 60%)' }} />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ background: '#FF8A3D' }}
                    >
                      <Icon name="BuildingLibraryIcon" size={24} style={{ color: '#fff' }} />
                    </div>
                    <h3 className="text-2xl font-headline font-bold" style={{ color: '#fff' }}>
                      Ayia Napa Underwater Museum (MUSAN)
                    </h3>
                  </div>
                </div>
              </div>
              <div className="p-8">
                <p className="leading-relaxed" style={{ color: '#C8DDE6' }}>
                  Ayia Napa Marine Protected Area also hosts the unique underwater museum (MUSAN), with dedicated navigation and safety signage for divers and visitors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Responsible Diving Practices */}
      <section className="py-12" style={{ background: '#0E1C2B' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2
                className="text-3xl lg:text-4xl font-headline font-bold mb-4"
                style={{ color: '#EAF4F8' }}
              >
                Responsible Diving Practices
              </h2>
              <p className="text-lg" style={{ color: '#C8DDE6' }}>
                Help protect Cyprus marine ecosystems by following these essential guidelines
              </p>
            </div>

            <div
              className="rounded-xl p-8"
              style={{
                background: '#0F2234',
                border: '1px solid rgba(111,211,224,0.20)',
              }}
            >
              <div className="space-y-4">
                {responsiblePractices.map((practice, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(111,211,224,0.10)' }}
                      >
                        <Icon name={practice.icon as any} size={18} style={{ color: '#6FD3E0' }} />
                      </div>
                    </div>
                    <p className="text-lg" style={{ color: '#EAF4F8' }}>{practice.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Mascot placeholder */}
            <div
              className="mt-8 rounded-xl p-8 text-center"
              style={{
                background: '#0F2234',
                border: '1px dashed rgba(111,211,224,0.20)',
              }}
            >
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: 'rgba(111,211,224,0.08)' }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="#6FD3E0" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12" aria-hidden="true">
                  <path d="M12 2C8 2 5 5 5 9c0 2 .5 3.5 1.5 4.5L5 22c2-1 3.5-2 4-3 .5 1 1.5 2 3 2s2.5-1 3-2c.5 1 2 2 4 3l-1.5-8.5C18.5 12.5 19 11 19 9c0-4-3-7-7-7z"/>
                  <path d="M9 14c-.5 2-1 3.5-1 5M15 14c.5 2 1 3.5 1 5M11 14c0 2-.2 4-.5 5.5M13 14c0 2 .2 4 .5 5.5"/>
                </svg>
              </div>
              <p className="text-sm italic" style={{ color: 'rgba(200,221,230,0.55)' }}>
                Cyprus Diving octopus mascot artwork - protecting marine life
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Conservation Initiatives */}
      <section className="py-12" style={{ background: '#0A0E14' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2
                className="text-3xl lg:text-4xl font-headline font-bold mb-4"
                style={{ color: '#EAF4F8' }}
              >
                Conservation Initiatives
              </h2>
              <p className="text-lg" style={{ color: '#C8DDE6' }}>
                Active programs protecting Cyprus underwater heritage
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {conservationInitiatives.map((initiative, index) => (
                <div
                  key={index}
                  className="rounded-xl p-6 transition-all duration-200"
                  style={{
                    background: '#0F2234',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.border = '1px solid rgba(111,211,224,0.25)';
                    el.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.border = '1px solid rgba(255,255,255,0.08)';
                    el.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(111,211,224,0.10)' }}
                      >
                        <Icon name={initiative.icon as any} size={24} style={{ color: '#6FD3E0' }} />
                      </div>
                    </div>
                    <div>
                      <h3
                        className="text-lg font-headline font-semibold"
                        style={{ color: '#EAF4F8' }}
                      >
                        {initiative.title}
                      </h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Community CTA */}
      <section className="py-12" style={{ background: '#0E1C2B' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div
              className="rounded-xl p-8"
              style={{
                background: '#0F2234',
                border: '1px solid rgba(111,211,224,0.15)',
              }}
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: '#FF8A3D' }}
                  >
                    <Icon name="PencilSquareIcon" size={24} style={{ color: '#0B1622' }} />
                  </div>
                </div>
                <div className="flex-1">
                  <h3
                    className="text-2xl font-headline font-bold mb-3"
                    style={{ color: '#EAF4F8' }}
                  >
                    Help us keep this guide accurate
                  </h3>
                  <p className="leading-relaxed mb-6" style={{ color: '#C8DDE6' }}>
                    Are you a local diver, instructor or dive centre in Cyprus? You can suggest corrections or submit updated information to help keep this independent guide accurate and current.
                  </p>
                  {/* Primary CTA */}
                  <Link
                    href="/homepage#community"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-200"
                    style={{ background: '#6FD3E0', color: '#0B1622', border: '1.5px solid #6FD3E0' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#5ec4d1'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#6FD3E0'; }}
                  >
                    <Icon name="PaperAirplaneIcon" size={20} />
                    <span>Submit information</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MarineConservationInteractive;